/**
 * i18n setup — i18next + react-i18next + browser-language detection.
 *
 * Strategy:
 *   1. Detect the user's preferred language from (a) localStorage override,
 *      (b) browser `navigator.language`, falling back to (c) English.
 *   2. Load English as the unconditional baseline (so `t()` never returns
 *      missing-key markers in production).
 *   3. Load Spanish + Portuguese for the launch markets (LATAM + Iberia).
 *   4. Persist the user's manual choice in localStorage so it sticks across
 *      visits regardless of browser language settings.
 *
 * Adding a language later means: drop a new JSON file under locales/,
 * import it below, register it in the resources block. No other code
 * changes required.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "@/locales/en.json";
import es from "@/locales/es.json";
import pt from "@/locales/pt.json";

export const SUPPORTED_LOCALES = [
  { code: "en", name: "English", native: "English" },
  { code: "es", name: "Spanish", native: "Español" },
  { code: "pt", name: "Portuguese", native: "Português" },
] as const;

export type LocaleCode = (typeof SUPPORTED_LOCALES)[number]["code"];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      pt: { translation: pt },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES.map((l) => l.code),
    nonExplicitSupportedLngs: true,   // "pt-BR" → falls back to "pt"
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      // Order matters: localStorage override beats browser, beats default.
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "curvle.locale",
      caches: ["localStorage"],
    },
  });

export default i18n;

/** Programmatic language change — used by the language picker. Persists
 *  to localStorage automatically because we listed it in detection.caches. */
export function setLocale(code: LocaleCode): Promise<unknown> {
  return i18n.changeLanguage(code);
}

/** Current active locale, normalised to a code from SUPPORTED_LOCALES. */
export function currentLocale(): LocaleCode {
  const lng = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  const found = SUPPORTED_LOCALES.find((l) => l.code === lng);
  return found ? found.code : "en";
}
